import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';

import { roomAtom } from '../video-sdk';

// these correspond to the class names set in the root-asset index.css
const HIDE_UI_ELEMENTS_CLASS_NAME = 'session-focused';
const USER_EVENTS = ['mousemove', 'keydown'];

const HIDE_ELEMENTS_TIMEOUT_DELAY_MS = 3000;

function hideNonEssentials() {
  document.body.classList.add(HIDE_UI_ELEMENTS_CLASS_NAME);
}

function showNonEssentials() {
  document.body.classList.remove(HIDE_UI_ELEMENTS_CLASS_NAME);
}

/**
 * Hides all non-essential elements, using the non-essential-elements props in
 * this file, when in a session.
 *
 * The non-essential elements will be hidden when the pointer is idle for 3
 * seconds, and shown when the pointer moves.
 */
export function useSessionFocus() {
  const [_hideElementsTimeout, setHideElementsTimeout] = useState<NodeJS.Timeout | null>(null);
  const isInSession = !!useAtomValue(roomAtom);

  useEffect(
    function handleSessionFocus() {
      const resetHideElementsTimeout = (nullify = false) => {
        setHideElementsTimeout((prevTimeout) => {
          if (prevTimeout) clearTimeout(prevTimeout);
          return nullify ? null : setTimeout(hideNonEssentials, HIDE_ELEMENTS_TIMEOUT_DELAY_MS);
        });
        showNonEssentials();
      };

      if (!isInSession) {
        resetHideElementsTimeout(true);
        return;
      }

      const abortController = new AbortController();
      USER_EVENTS.forEach((eventName) => {
        document.addEventListener(eventName, () => resetHideElementsTimeout(), {
          signal: abortController.signal,
        });
      });

      return () => abortController.abort();
    },
    [isInSession],
  );
}
